import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, type ApiClient } from '../apiClient';

/**
 * Reading the assistant's answers aloud.
 *
 * The audio comes from our own server, not from the browser's built-in
 * speech synthesis: system voices on a cheap Android read this like a train
 * announcement, and the person we are building for is the one who will give
 * up if it sounds like a machine. The server also refuses to speak anything
 * the assistant did not itself say, which is what keeps a member's own
 * message text out of a third-party voice service.
 */
export function useSpokenAnswer(api: ApiClient) {
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  // A deployment without an ElevenLabs key would otherwise show a Listen
  // button that does nothing at all. We cannot know before the first attempt,
  // so we ask once and then stop offering it — a button that fails silently
  // is worse on this product than no button.
  // Starts false and is switched on by the first answer that says the server
  // has a voice — so the button appears only when it will actually work.
  const [available, setAvailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setSpeakingIndex(null);
  }, []);

  // A panel that keeps talking after it is closed is worse than one that does
  // not talk at all.
  useEffect(() => stop, [stop]);

  const speak = useCallback(
    async (index: number, text: string) => {
      if (speakingIndex === index) {
        stop();
        return;
      }
      stop();
      setSpeakingIndex(index);
      try {
        const blob = await api.postBlob('/api/assistant/speak', { text });
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = stop;
        audio.onerror = stop;
        await audio.play();
      } catch (err) {
        if (err instanceof ApiError && err.status === 400 &&
            /not configured/i.test(JSON.stringify(err.body ?? ''))) {
          setAvailable(false);
        }
        stop();
      }
    },
    [api, speakingIndex, stop],
  );

  return { speak, stop, speakingIndex, available, setAvailable };
}

/**
 * Dictating a question.
 *
 * Uses the browser's own recognition (free, no audio leaves the device on
 * Safari; Chrome sends it to Google's service — which is why this is opt-in
 * per press and never listens on its own). Absent in Firefox, so the button
 * is simply not rendered there rather than being present and dead.
 */
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

type RecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useDictation(onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const supported = recognitionCtor() !== null;

  const toggle = useCallback(() => {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const Ctor = recognitionCtor();
    if (Ctor === null) return;
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-GB';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      let text = '';
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i][0].transcript;
      }
      if (text.trim() !== '') onText(text.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  }, [listening, onText]);

  useEffect(() => () => recRef.current?.stop(), []);

  return { supported, listening, toggle };
}
